import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchUserPhoto, fetchUserProfile, UserProfile } from '../utils/graphApi';

interface UseUserProfileReturn {
  userPhoto: string | null;
  userProfile: UserProfile | null;
  loadingPhoto: boolean;
  loadingProfile: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useUserProfile = (): UseUserProfileReturn => {
  const { user } = useAuth();
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!user) return;

    setLoadingPhoto(true);
    setLoadingProfile(true);
    setError(null);

    try {
      // Fetch photo and profile in parallel
      const [photo, profile] = await Promise.all([
        fetchUserPhoto(),
        fetchUserProfile(),
      ]);

      setUserPhoto(photo);
      setUserProfile(profile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user data';
      setError(errorMessage);
      console.error('Failed to load user profile data:', err);
    } finally {
      setLoadingPhoto(false);
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  return {
    userPhoto,
    userProfile,
    loadingPhoto,
    loadingProfile,
    error,
    refetch: fetchData,
  };
};
